import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

// Student-side: my applications
export function useMyApplications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["my_job_applications", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_applications")
        .select("*, job_posts(title, employment_type, work_model, location_city, location_state, partner_orgs(name))")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const apply = useMutation({
    mutationFn: async ({ jobId, consentVisibility = true }: { jobId: string; consentVisibility?: boolean }) => {
      if (!user) throw new Error("Not authenticated");
      // Insert application
      const { data, error } = await supabase
        .from("job_applications")
        .insert({ job_id: jobId, user_id: user.id, consent_visibility: consentVisibility, status: "aplicada" })
        .select()
        .single();
      if (error) throw error;

      // Create status event
      await supabase.from("application_status_events").insert({
        application_id: data.id,
        from_status: null,
        to_status: "aplicada",
        actor_user_id: user.id,
        note: "Candidatura enviada",
      });

      // Notify student by email (non-blocking)
      try {
        await supabase.functions.invoke("notify-job-application", {
          body: { applicationId: data.id, eventType: "applied" },
        });
      } catch (e) {
        console.error("Falha ao enviar notificação:", e);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my_job_applications", user?.id] });
    },
  });

  return { applications: query.data ?? [], isLoading: query.isLoading, apply };
}

// Partner-side: applications for a specific job
export function useJobCandidaturas(jobId?: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["job_candidaturas", jobId],
    enabled: !!jobId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_applications")
        .select("*, profiles(full_name, city, state, objective, bio, profile_status)")
        .eq("job_id", jobId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ applicationId, fromStatus, toStatus, note }: { applicationId: string; fromStatus: string; toStatus: string; note?: string }) => {
      if (!user) throw new Error("Not authenticated");
      const { error: updateError } = await supabase
        .from("job_applications")
        .update({ status: toStatus })
        .eq("id", applicationId);
      if (updateError) throw updateError;

      const { error: eventError } = await supabase
        .from("application_status_events")
        .insert({
          application_id: applicationId,
          from_status: fromStatus,
          to_status: toStatus,
          actor_user_id: user.id,
          note: note || null,
        });
      if (eventError) throw eventError;

      // Notify student of status change (non-blocking)
      try {
        await supabase.functions.invoke("notify-job-application", {
          body: { applicationId, eventType: "status_changed", newStatus: toStatus, note: note || undefined },
        });
      } catch (e) {
        console.error("Falha ao enviar notificação:", e);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["job_candidaturas", jobId] });
    },
  });

  const statusEvents = useQuery({
    queryKey: ["application_events", jobId],
    enabled: !!jobId,
    queryFn: async () => {
      // Get all application ids for this job first
      const apps = query.data?.map((a) => a.id) ?? [];
      if (apps.length === 0) return [];
      const { data, error } = await supabase
        .from("application_status_events")
        .select("*")
        .in("application_id", apps)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  return {
    candidaturas: query.data ?? [],
    isLoading: query.isLoading,
    updateStatus,
    events: statusEvents.data ?? [],
  };
}
